import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { toast } from "sonner";
import { Anchor, ArrowRight } from "lucide-react";

const COVERS = {
  signup: "https://static.prod-images.emergentagent.com/jobs/6725bdb3-5390-41ce-b709-55530376423a/images/171f9522b5b112843396cab50f4b1a232f54da524bac42df946253a9cc2e30f6.jpeg",
  signin: "https://static.prod-images.emergentagent.com/jobs/6725bdb3-5390-41ce-b709-55530376423a/images/3c0e38ecde0438b4f77c27988b6eff38c470869f3a31458f4a3f4b09413433f0.jpeg",
};

export default function Auth({ initialMode = "signin" }) {
  const { login, register } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState({ first_name: "", last_name: "", email: "", phone: "", password: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const isSignup = mode === "signup";
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const cover = isSignup ? COVERS.signup : COVERS.signin;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const res = isSignup ? await register(form) : await login(form.email, form.password);
    setBusy(false);
    if (res.ok) {
      toast.success(isSignup ? t.auth.signUp : t.nav.login);
      navigate(res.user.onboarding_completed ? "/dashboard" : "/onboarding");
    } else setErr(res.error);
  };

  const Tab = ({ m, label }) => (
    <button
      type="button"
      onClick={() => { setMode(m); setErr(""); }}
      className={`flex-1 py-2.5 rounded-full text-sm font-display transition-all ${mode === m ? "bg-[#0B192C] text-white shadow-sm" : "text-slate-500 hover:text-[#0B192C]"}`}
      data-testid={`auth-tab-${m}`}
    >
      {label}
    </button>
  );

  return (
    <div className="grid lg:grid-cols-2 min-h-[calc(100vh-4rem)]" data-testid="auth-page">
      {/* Form side */}
      <div className="flex items-center justify-center px-6 py-12 lg:py-0 order-2 lg:order-1">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 rounded-2xl overflow-hidden aspect-[16/9] relative">
            <img src={cover} alt="Bevela" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/70 to-transparent" />
          </div>

          <p className="eyebrow">Bevela</p>
          <h1 className="font-serif text-3xl sm:text-4xl mt-3 text-[#0B192C]" data-testid="auth-title">
            {isSignup ? t.auth.registerTitle : t.auth.loginTitle}
          </h1>
          <p className="mt-2 text-slate-500 text-sm">
            {isSignup ? t.auth.registerSubtitle : t.auth.loginSubtitle}
          </p>

          <div className="mt-8 flex gap-1 p-1 rounded-full bg-slate-100" data-testid="auth-toggle">
            <Tab m="signin" label={t.auth.signIn} />
            <Tab m="signup" label={t.auth.signUp} />
          </div>

          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="auth-form">
            {isSignup && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label-elegant">{t.auth.firstName}</label>
                  <input required value={form.first_name} onChange={set("first_name")} className="input-elegant" data-testid="auth-first-name" />
                </div>
                <div>
                  <label className="label-elegant">{t.auth.lastName}</label>
                  <input required value={form.last_name} onChange={set("last_name")} className="input-elegant" data-testid="auth-last-name" />
                </div>
              </div>
            )}
            <div>
              <label className="label-elegant">{t.auth.email}</label>
              <input required type="email" value={form.email} onChange={set("email")} className="input-elegant" data-testid="auth-email" autoComplete="email" />
            </div>
            {isSignup && (
              <div>
                <label className="label-elegant">{t.auth.phone}</label>
                <input value={form.phone} onChange={set("phone")} className="input-elegant" data-testid="auth-phone" />
              </div>
            )}
            <div>
              <label className="label-elegant">{t.auth.password}</label>
              <input required type="password" minLength={6} value={form.password} onChange={set("password")} className="input-elegant" data-testid="auth-password" autoComplete={isSignup ? "new-password" : "current-password"} />
            </div>
            {err && <p className="text-sm text-red-600" data-testid="auth-error">{err}</p>}
            <button disabled={busy} type="submit" className="btn-primary w-full justify-center disabled:opacity-60" data-testid="auth-submit">
              {isSignup ? t.auth.signUp : t.auth.signIn} <ArrowRight className="h-4 w-4" />
            </button>
            {!isSignup && (
              <div className="text-center">
                <Link to="/recuperar" className="text-sm text-slate-500 hover:text-[#0B192C]" data-testid="auth-forgot">{t.auth.forgotLink}</Link>
              </div>
            )}
          </form>

          <p className="mt-8 text-sm text-slate-500 text-center">
            {isSignup ? t.auth.haveAccount : t.auth.noAccount}{" "}
            <button type="button" onClick={() => { setMode(isSignup ? "signin" : "signup"); setErr(""); }} className="text-[#0B192C] font-medium underline underline-offset-4" data-testid="auth-switch">
              {isSignup ? t.auth.signIn : t.auth.signUp}
            </button>
          </p>
        </div>
      </div>

      {/* Image side (desktop) */}
      <div className="relative hidden lg:block order-1 lg:order-2 overflow-hidden">
        <img src={COVERS.signin} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isSignup ? "opacity-0" : "opacity-100"}`} />
        <img src={COVERS.signup} alt="" className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${isSignup ? "opacity-100" : "opacity-0"}`} />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B192C]/85 via-[#0B192C]/25 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <p className="font-serif text-3xl xl:text-4xl text-white leading-tight max-w-md">
            Tu barco. Tu mar. Tu gente.
          </p>
          <p className="mt-3 text-white/80 max-w-sm leading-relaxed">
            Copropiedad de embarcaciones en el Mediterráneo, sin complicaciones.
          </p>
          <div className="mt-6 flex items-center gap-2 text-white/90">
            <Anchor className="h-4 w-4" /> <span className="font-display tracking-wide">Bevela. Vívela.</span>
          </div>
        </div>
      </div>
    </div>
  );
}
